export interface SessionFlowRoles {
  teacher?: string;
  students?: string;
  materials?: string;
}

export interface SessionFlowStep {
  title: string;
  durationMin?: number;
  durationLabel?: string;
  roles: SessionFlowRoles;
}

export interface ParsedSessionDetail {
  summary?: string;
  /** Código OA extraído del resumen o metadatos (ej. "OA 13") */
  oaCode?: string;
  /** Objetivo de aprendizaje sin el prefijo OA */
  learningObjective?: string;
  flow: SessionFlowStep[];
  evidence?: string;
  tips?: string;
}

export function normalizeOaCode(raw: string): string {
  const digits = raw.match(/\d+[a-zA-Z0-9]*/)?.[0];
  return digits ? `OA ${digits}` : raw.replace(/\s+/g, ' ').trim();
}

export function parseLearningObjective(summary: string): {
  oaCode?: string;
  learningObjective: string;
} {
  const trimmed = summary.trim();
  if (!trimmed) return { learningObjective: '' };

  const prefixMatch = trimmed.match(/^\s*(OA\s*\d+[a-zA-Z0-9]*)\s*[:\-–—]\s*(.+)$/i);
  if (prefixMatch) {
    return {
      oaCode: normalizeOaCode(prefixMatch[1]),
      learningObjective: prefixMatch[2].trim(),
    };
  }

  const inlineMatch = trimmed.match(/\b(OA\s*\d+[a-zA-Z0-9]*)\b/i);
  if (inlineMatch) {
    const withoutOa = trimmed
      .replace(inlineMatch[0], '')
      .replace(/^[:\-–—,\s]+/, '')
      .trim();
    return {
      oaCode: normalizeOaCode(inlineMatch[1]),
      learningObjective: withoutOa || trimmed,
    };
  }

  return { learningObjective: trimmed };
}

export function extractOaCodeFromText(text?: string): string | undefined {
  if (!text?.trim()) return undefined;
  const match = text.match(/\b(OA\s*\d+[a-zA-Z0-9]*)\b/i);
  return match ? normalizeOaCode(match[1]) : undefined;
}

const INLINE_RESUMEN = /^#{0,3}\s*Resumen\s*:?\s*(.*)$/i;
const INLINE_EVIDENCIA = /^#{0,3}\s*Evidencia(?:\s+de\s+avance)?\s*:?\s*(.*)$/i;
const INLINE_TIPS = /^#{0,3}\s*(?:Tips|Consejos)\s*:?\s*(.*)$/i;
const HEADER_FLUJO = /^#{0,3}\s*Flujo de la sesi[oó]n\s*:?\s*$/i;
const STEP_START = /^\s*(?:[•\-\*]|\d+\.)\s+/;
const ROLE_DOCENTE = /^Docente\s*:?\s*(.*)$/i;
const ROLE_ESTUDIANTES = /^Estudiantes\s*:?\s*(.*)$/i;
const ROLE_MATERIALES = /^Materiales\s*:?\s*(.*)$/i;
const ROLE_DOCENTE_HEADER = /^Docente\s*:?\s*$/i;
const ROLE_ESTUDIANTES_HEADER = /^Estudiantes\s*:?\s*$/i;
const ROLE_MATERIALES_HEADER = /^Materiales\s*:?\s*$/i;
const DURATION_ONLY_LINE = /^\~?\s*(\d+)\s*min\s*$/i;
const DURATION_PAREN = /\(\s*~?\s*(\d+)\s*min\s*\)\s*$/i;
const DURATION_SUFFIX = /\s~?\s*(\d+)\s*min\s*$/i;

function parseStepTitle(line: string): Pick<SessionFlowStep, 'title' | 'durationMin' | 'durationLabel'> {
  const parenMatch = line.match(DURATION_PAREN);
  if (parenMatch) {
    const durationMin = Number.parseInt(parenMatch[1], 10);
    const title = line.slice(0, line.indexOf(parenMatch[0])).trim();
    return {
      title: title || line.trim(),
      durationMin,
      durationLabel: `~${durationMin} min`,
    };
  }

  const suffixMatch = line.match(DURATION_SUFFIX);
  if (suffixMatch) {
    const durationMin = Number.parseInt(suffixMatch[1], 10);
    const title = line.slice(0, line.indexOf(suffixMatch[0])).trim();
    return {
      title: title || line.trim(),
      durationMin,
      durationLabel: `~${durationMin} min`,
    };
  }

  return { title: line.trim() };
}

function parseFlowSteps(flowText: string): SessionFlowStep[] {
  const lines = flowText.split('\n');
  const blocks: string[][] = [];
  let current: string[] | null = null;

  for (const line of lines) {
    if (STEP_START.test(line)) {
      if (current?.length) blocks.push(current);
      current = [line.replace(STEP_START, '').trim()];
      continue;
    }

    if (!line.trim()) continue;

    if (!current) {
      current = [line.trim()];
      continue;
    }

    current.push(line.trim());
  }

  if (current?.length) blocks.push(current);

  return blocks.map((block) => {
    let titleLine = block[0] || '';
    let cursor = 1;

    const durationOnly = block[cursor]?.match(DURATION_ONLY_LINE);
    if (durationOnly) {
      titleLine = `${titleLine} (~${durationOnly[1]} min)`;
      cursor += 1;
    }

    const rest = block.slice(cursor);
    const roles: SessionFlowRoles = {};
    let activeRole: 'teacher' | 'students' | 'materials' | null = null;

    const appendRole = (key: keyof SessionFlowRoles, chunk: string) => {
      const text = chunk.trim();
      if (!text) return;
      roles[key] = roles[key] ? `${roles[key]} ${text}` : text;
    };

    for (const line of rest) {
      const docente = line.match(ROLE_DOCENTE);
      const estudiantes = line.match(ROLE_ESTUDIANTES);
      const materiales = line.match(ROLE_MATERIALES);

      if (ROLE_DOCENTE_HEADER.test(line)) {
        activeRole = 'teacher';
        continue;
      }
      if (ROLE_ESTUDIANTES_HEADER.test(line)) {
        activeRole = 'students';
        continue;
      }
      if (ROLE_MATERIALES_HEADER.test(line)) {
        activeRole = 'materials';
        continue;
      }

      if (docente) {
        activeRole = null;
        appendRole('teacher', docente[1]);
      } else if (estudiantes) {
        activeRole = null;
        appendRole('students', estudiantes[1]);
      } else if (materiales) {
        activeRole = null;
        appendRole('materials', materiales[1]);
      } else if (activeRole) {
        appendRole(activeRole, line);
      }
    }

    return {
      ...parseStepTitle(titleLine),
      roles,
    };
  });
}

export function parseSessionDetail(rawContent: string): ParsedSessionDetail {
  const text = String(rawContent || '').trim();
  if (!text) return { flow: [] };

  const summaryLines: string[] = [];
  const flowLines: string[] = [];
  const evidenceLines: string[] = [];
  const tipsLines: string[] = [];

  type Section = 'none' | 'summary' | 'flow' | 'evidence' | 'tips';
  let section: Section = 'none';

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    const resumenMatch = trimmed.match(INLINE_RESUMEN);
    if (resumenMatch) {
      section = 'summary';
      if (resumenMatch[1]?.trim()) summaryLines.push(resumenMatch[1].trim());
      continue;
    }

    if (HEADER_FLUJO.test(trimmed)) {
      section = 'flow';
      continue;
    }

    const evidenciaMatch = trimmed.match(INLINE_EVIDENCIA);
    if (evidenciaMatch) {
      section = 'evidence';
      if (evidenciaMatch[1]?.trim()) evidenceLines.push(evidenciaMatch[1].trim());
      continue;
    }

    const tipsMatch = trimmed.match(INLINE_TIPS);
    if (tipsMatch) {
      section = 'tips';
      if (tipsMatch[1]?.trim()) tipsLines.push(tipsMatch[1].trim());
      continue;
    }

    if (!trimmed) continue;

    switch (section) {
      case 'summary':
        summaryLines.push(trimmed);
        break;
      case 'flow':
        flowLines.push(line);
        break;
      case 'evidence':
        evidenceLines.push(trimmed);
        break;
      case 'tips':
        tipsLines.push(trimmed);
        break;
      default:
        break;
    }
  }

  const joinSection = (lines: string[]) => lines.join(' ').replace(/\s+/g, ' ').trim();
  const summary = joinSection(summaryLines) || undefined;
  const objectiveParts = summary ? parseLearningObjective(summary) : undefined;

  return {
    summary,
    oaCode: objectiveParts?.oaCode,
    learningObjective: objectiveParts?.learningObjective || summary,
    flow: parseFlowSteps(flowLines.join('\n')),
    evidence: joinSection(evidenceLines) || undefined,
    tips: joinSection(tipsLines) || undefined,
  };
}
